// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useEffect } from 'react';
import UniversalModal from './UniversalModal';
import CompactSystemStatus from './CompactSystemStatus';
import ShipNamingModal from './ShipNamingModal';
import ProgressBar from './ProgressBar';
import { useShipHealth } from '../contexts/ShipHealthContext';
import { useCredits } from '../contexts/CreditsContext';
import './ShipManagementScreen.css';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

/**
 * Ship Management Screen
 * 4 Tabs: Overview, Customization, Loadout, History
 */
const ShipManagementScreen = ({ onClose, embedded = false }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showNamingModal, setShowNamingModal] = useState(false);
  useShipHealth();
  useCredits();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '🚀' },
    { id: 'log', label: "Captain's Log", icon: '📜' },
    { id: 'history', label: 'History', icon: '📊' }
  ];

  const content = (
    <>
      {activeTab === 'overview' && (
        <OverviewTab onRenameShip={() => setShowNamingModal(true)} />
      )}
      
      {activeTab === 'log' && (
        <CaptainsLogTab />
      )}
      
      {activeTab === 'history' && (
        <HistoryTab />
      )}
      
      {showNamingModal && (
        <ShipNamingModal onClose={() => setShowNamingModal(false)} />
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="ship-management-embedded">
        <div className="embedded-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`embedded-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.icon}</span> {tab.label}
            </button>
          ))}
        </div>
        {content}
      </div>
    );
  }

  return (
    <UniversalModal
      title="SHIP MANAGEMENT"
      subtitle="Ship status, customization, and history"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onClose={onClose}
      className="ship-management-screen"
    >
      {content}
    </UniversalModal>
  );
};

// Overview Tab Component - Uses CompactSystemStatus with ship damage visualization
const OverviewTab = ({ onRenameShip }) => {
  const [shipName] = useState(() => localStorage.getItem(STORAGE_KEYS.SHIP_NAME) || 'USS Enterprise');
  const [stats] = useState(() => ({
    systemsVisited: parseInt(localStorage.getItem(STORAGE_KEYS.SYSTEMS_VISITED) || '0'),
    battlesWon: parseInt(localStorage.getItem(STORAGE_KEYS.BATTLES_WON) || '0')
  }));
  
  const { systems: shipSystems } = useShipHealth();
  const { credits, removeCredits } = useCredits();

  const systems = shipSystems.map(sys => ({
    id: sys.id,
    name: sys.name,
    value: Math.round((sys.health / sys.maxHealth) * 100),
    icon: sys.id === 'HULL' ? '🛡️' :
          sys.id === 'SHIELDS' ? '⚡' :
          sys.id === 'ENGINES' ? '🚀' :
          sys.id === 'WEAPONS' ? '⚔️' :
          sys.id === 'LIFE_SUPPORT' ? '💨' :
          sys.id === 'SENSORS' ? '📡' :
          sys.id === 'POWER' ? '🔋' : '❓',
    cost: Math.ceil((sys.maxHealth - sys.health) * 10)
  }));

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (value) => {
    if (value >= 80) return '#00ff88';
    if (value >= 60) return '#ffaa00';
    if (value >= 40) return '#ff6600';
    return '#ff4444';
  };

  const handleRepairSystem = (systemId, cost) => {
    // eslint-disable-next-line no-undef
    if (credits >= cost) {
      removeCredits(cost);
      // Repair handled by CompactSystemStatus
      window.location.reload(); // Refresh to show updated health
    }
  };

  return (
    <div className="tab-content overview-tab">
      {/* Top Section: Stats + Ship Visualization */}
      <div className="overview-top-section">
        {/* Left: Quick Stats */}
        <div className="quick-stats-left">
          <h3>📊 Ship Status</h3>
          <div className="stats-column">
            <div className="stat-item">
              <span className="stat-label">Ship Name:</span>
              <span 
                className="stat-value clickable-ship-name" 
                onClick={onRenameShip}
                title="Click to rename ship"
              >
                {shipName} ✏️
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Class:</span>
              <span className="stat-value">Explorer</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Systems Visited:</span>
              <span className="stat-value">{stats.systemsVisited}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Battles Won:</span>
              <span className="stat-value">{stats.battlesWon}</span>
            </div>
          </div>
        </div>

        {/* Right: Ship Visualization */}
        <div className="ship-status-container">
          <CompactSystemStatus showRepairButton={false} />
        </div>
      </div>

      {/* Individual System Repairs */}
      <div className="repairs-section">
        <h3>🔨 System Repairs</h3>
        <p className="repair-description">Repair individual systems. Systems auto-repair slowly in orbit (1% per second).</p>
        <div className="repairs-grid">
          {systems.map(system => (
            <div key={system.id} className="repair-card">
              <div className="repair-header">
                <span className="repair-icon">{system.icon}</span>
                <span className="repair-name">{system.name}</span>
              </div>
              <div className="repair-status">
                <ProgressBar 
                  value={system.value}
                  max={100}
                  color="auto"
                  showLabel={true}
                  size="medium"
                />
              </div>
              <button 
                className="repair-button"
                disabled={system.value >= 100 || credits < system.cost}
                onClick={() => handleRepairSystem(system.id, system.cost)}
              >
                {system.value >= 100 ? '✓ Optimal' : `Repair (${system.cost} CR)`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Captain's Log Tab Component
const CaptainsLogTab = () => {
  const [logEntries, setLogEntries] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CAPTAINS_LOG);
    return saved ? JSON.parse(saved) : [];
  });
  const [newEntry, setNewEntry] = useState('');
  const [currentLocation] = useState(() => localStorage.getItem(STORAGE_KEYS.CURRENT_SYSTEM_NAME) || 'Unknown System');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CAPTAINS_LOG, JSON.stringify(logEntries));
  }, [logEntries]);

  const addEntry = () => {
    if (newEntry.trim()) {
      const entry = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        location: currentLocation,
        text: newEntry.trim(),
        type: 'manual'
      };
      setLogEntries([entry, ...logEntries]);
      setNewEntry('');
    }
  };

  const deleteEntry = (id) => {
    setLogEntries(logEntries.filter(e => e.id !== id));
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="tab-content captains-log-tab">
      <h3>📜 Captain's Log</h3>
      <p className="tab-description">Record your journey through the stars. Personal notes and observations.</p>
      
      {/* New Entry Form */}
      <div className="log-entry-form">
        <textarea
          className="log-textarea"
          placeholder="Captain's log, stardate... What happened today?"
          value={newEntry}
          onChange={(e) => setNewEntry(e.target.value)}
          rows={4}
        />
        <div className="log-form-actions">
          <span className="log-location">📍 {currentLocation}</span>
          <button 
            className="log-submit-button"
            onClick={addEntry}
            disabled={!newEntry.trim()}
          >
            Add Entry
          </button>
        </div>
      </div>

      {/* Log Entries List */}
      <div className="log-entries-list">
        {logEntries.length === 0 ? (
          <div className="empty-log">
            <p>No log entries yet.</p>
            <p>Start recording your journey through the cosmos!</p>
          </div>
        ) : (
          logEntries.map(entry => (
            <div key={entry.id} className="log-entry-card">
              <div className="log-entry-header">
                <div className="log-entry-meta">
                  <span className="log-entry-date">{formatDate(entry.timestamp)}</span>
                  <span className="log-entry-location">📍 {entry.location}</span>
                </div>
                <button 
                  className="log-delete-button"
                  onClick={() => deleteEntry(entry.id)}
                  title="Delete entry"
                >
                  ✕
                </button>
              </div>
              <div className="log-entry-text">
                {entry.text}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// History Tab Component
const HistoryTab = () => {
  const [stats] = useState(() => {
    return {
      systemsVisited: parseInt(localStorage.getItem(STORAGE_KEYS.SYSTEMS_VISITED) || '0'),
      battlesWon: parseInt(localStorage.getItem(STORAGE_KEYS.BATTLES_WON) || '0'),
      battlesLost: parseInt(localStorage.getItem(STORAGE_KEYS.BATTLES_LOST) || '0'),
      distanceTraveled: parseFloat(localStorage.getItem(STORAGE_KEYS.TOTAL_DISTANCE_TRAVELED) || '0').toFixed(1),
      creditsEarned: parseInt(localStorage.getItem(STORAGE_KEYS.TOTAL_CREDITS_EARNED) || '0'),
      aliensEncountered: parseInt(localStorage.getItem(STORAGE_KEYS.ALIENS_ENCOUNTERED) || '0'),
      tradesCompleted: parseInt(localStorage.getItem(STORAGE_KEYS.TRADES_COMPLETED) || '0'),
      planetsExplored: parseInt(localStorage.getItem(STORAGE_KEYS.PLANETS_EXPLORED) || '0')
    };
  });

  const achievements = [
    { name: 'First Contact', desc: 'Encounter your first alien species', unlocked: stats.aliensEncountered > 0 },
    { name: 'Explorer', desc: 'Visit 10 star systems', unlocked: stats.systemsVisited >= 10 },
    { name: 'Warrior', desc: 'Win 5 battles', unlocked: stats.battlesWon >= 5 },
    { name: 'Merchant', desc: 'Complete 10 trades', unlocked: stats.tradesCompleted >= 10 },
    { name: 'Voyager', desc: 'Travel 1000 light-years', unlocked: parseFloat(stats.distanceTraveled) >= 1000 }
  ];

  return (
    <div className="tab-content history-tab">
      <h3>📜 Ship History</h3>
      <p className="tab-description">Your journey through the stars.</p>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🌟</div>
          <div className="stat-value">{stats.systemsVisited}</div>
          <div className="stat-label">Systems Visited</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">⚔️</div>
          <div className="stat-value">{stats.battlesWon}</div>
          <div className="stat-label">Battles Won</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">💀</div>
          <div className="stat-value">{stats.battlesLost}</div>
          <div className="stat-label">Battles Lost</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🚀</div>
          <div className="stat-value">{stats.distanceTraveled}</div>
          <div className="stat-label">Light-Years Traveled</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-value">{stats.creditsEarned.toLocaleString()}</div>
          <div className="stat-label">Credits Earned</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">👽</div>
          <div className="stat-value">{stats.aliensEncountered}</div>
          <div className="stat-label">Aliens Met</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🤝</div>
          <div className="stat-value">{stats.tradesCompleted}</div>
          <div className="stat-label">Trades Completed</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🌍</div>
          <div className="stat-value">{stats.planetsExplored}</div>
          <div className="stat-label">Planets Explored</div>
        </div>
      </div>
      
      <div className="achievements-section">
        <h4>🏆 Achievements</h4>
        <div className="achievements-list">
          {achievements.map((achievement, index) => (
            <div key={index} className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}>
              <div className="achievement-icon">{achievement.unlocked ? '✓' : '🔒'}</div>
              <div className="achievement-info">
                <div className="achievement-name">{achievement.name}</div>
                <div className="achievement-desc">{achievement.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShipManagementScreen;
